<template>
  <div>

    <Portal to="breadcrumb_portfolioDetail">
      {{ portfolio ? portfolio.name : '' }}
    </Portal>

    <fin-data-loader :resolver="loadPortfolio" v-if="!this.$route.meta.newPage" overlay ref="loader">

      <div v-if="portfolio && !loadingPortfolio">

        <div v-if="!addToPortfolio">
          <fin-choose-selector
            v-model="addToPortfolioDialog"
            :options="assetDistribution"
            :preSelectedItem="selectedAssetClass ? selectedAssetClass : assetDistribution[0]"
            :title="`Wertpapier zu ${this.portfolio.name} hinzufügen`"
            ok-button-label="Weiter"
            select-box-item-title="name"
            select-box-item-value="id"
            select-box-title="Assetklasse"
            @submitSelectedItem="submitAssetClass"
          />

          <v-container v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
                       v-style:paddingRight="$valueByBreakpoint($finPadding.right)"
                       class="fin-color-primary-light"
                       fluid>
            <v-row v-if="!addToPortfolio"
                   :class="$vuetify.breakpoint.smAndDown ? 'pt-5' : 'pt-10'">
              <v-col cols="9" sm="10" md="7" lg="8">
                <fin-editable-property ref="rename"
                                       :editFunction="(val) => updatePortfolioName(portfolio, val)"
                                       :orginal-value="portfolio.name"
                                       :value="portfolio.name"
                                       big-value-size
                                       class="name"
                                       submit-button="Umbenennen"
                                       title="Portfolio umbenennen">
                  <template #default="{model,change}" class="name">
                    <slot :item="portfolio" name="item-title"/>
                    <v-text-field
                      :rules="[ v => !!v || 'Name wird benötigt']"
                      :value="model"
                      label="Portfolio Name"
                      @input="change"
                    />
                  </template>
                  <template #subOriginalValue>
                    <fin-realtime-indicator v-if="!isInactivePortfolio"
                                            :instruments="[...portfolio.shareEntries.items.map(item => item.instrument),
                                            ...portfolio.etfEntries.items.map(item => item.instrument)]"
                                            class="realtime"
                                            trading-platform="tradegate"/>
                  </template>
                </fin-editable-property>
                <div v-if="!isInactivePortfolio" class="last-change-text">
                  Letzte Anpassung: {{ portfolio.updatedAt|datetime }}
                </div>
              </v-col>
              <v-col cols="3" sm="2" md="5" lg="4">
                <div class="text-right">
                  <v-btn v-if="$vuetify.breakpoint.mdAndUp"
                         class="mt-2 mr-2"
                         dark
                         :disabled="isInactivePortfolio"
                         @click="manageMonitoring('monitoringButton')">
                    <v-icon v-if="isMonitoringAllowed"
                            class="mr-1">icon-notification
                    </v-icon>
                    <fin-subscription-lock v-else/>
                    Monitoring
                  </v-btn>
                  <v-btn v-if="$vuetify.breakpoint.mdAndUp"
                         class="mt-2"
                         dark
                         :disabled="isInactivePortfolio || !hasInstrumentsEntries"
                         @click="gotoTabWithId('BACKTESTING')">
                    <fin-subscription-lock v-if="!isBacktestingEnabled"/>
                    Backtesting
                  </v-btn>

                  <v-menu :nudge-bottom=10
                          :nudge-left=50
                          :offset-y="true"
                          bottom>
                    <template #activator="{ on }">
                      <v-btn class="ml-4 mt-2 fin-portfolio-contextmenu"
                             icon
                             outlined
                             tile
                             v-on="on"
                             @click.native="contextMenuClicked">
                        <v-icon>icon-contextmenu</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item :disabled="isInactivePortfolio" @click="showRename">
                        <v-list-item-title>
                          Umbenennen
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        :disabled="portfolio.status && portfolio.status.calculationStatus !== 'OK' && portfolio.status.calculationStatus !== 'FAILED'"
                        @click="startDeletePortfolio">
                        <v-list-item-title>
                          Löschen
                        </v-list-item-title>
                      </v-list-item>
                      <v-divider/>
                      <v-list-item
                        :disabled="isInactivePortfolio || (portfolio.status && portfolio.status.calculationStatus !== 'OK')"
                        @click="addToPortfolioClicked">
                        <v-list-item-title>
                          Wertpapiere hinzufügen
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item :disabled="isInactivePortfolio || !portfolio.transactions"
                                   @click="manageTransactions">
                        <v-list-item-title>
                          Historie
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item :disabled="isInactivePortfolio"
                                   @click="manageMonitoring('contextmenu')">
                        <v-list-item-title>
                          Monitoring ein-/ausschalten
                          <fin-subscription-lock v-if="!isMonitoringAllowed" color="fin-color-primary-dark"/>
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="!isAutoDividendenServiceEnabled"
                                   @click="showDividendenServiceAboUpgradeDialog">
                        <v-list-item-title>
                          Dividendenservice
                          <fin-subscription-lock color="fin-color-primary-dark"/>
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="!isIos && !isMobile"
                                   :disabled="!excelDownloadEnabled"
                                   @click="exportExcel">
                        <v-list-item-title>
                          Als Excel speichern
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </v-col>
            </v-row>
          </v-container>

          <fin-banner v-if="!addToPortfolio && computedBannerMessagesList.length > 0"
                      :background-color="$finColors.lightGrayBlue"
                      single-line
                      icon="icon-information"
                      @closeBanner="showPortfolioStatusBanner = false"
                      banner-position="fixed"
                      bannerPositionTop="56px"
                      bannerPaddingTop="8px"
          >
            <template #icon>
              <fin-logo-sign class="mr-5" loading border dark/>
            </template>
            <div v-for="(item, index) in computedBannerMessagesList"
                 :key="index" class="d-inline-block">
              <p class="mb-0" v-if="item.status && item.status.calculationStatus !== 'OK'">
                {{ getPortfolioStatusReadableMessage(item.status.calculationStatus) }}
              </p>
            </div>
          </fin-banner>

          <fin-transactions-list-dialog v-model="showTransactionsDialog"
                                        v-if="showTransactionsDialog"
                                        @updatePortfolio="reloadPortfolio"
                                        :instruments="instruments"
                                        :selected-view-id="selectedViewId"
                                        :portfolio-id="id"
                                        :full-screen="$vuetify.breakpoint.xs"
                                        global-transactions/>

          <fin-dialog v-model="showMonitoringManagementDialog"
                      :max-width="450">
            <template #title>
              <fin-lexicon-text auto-solve
                                :text="`Monitoring ${monitoringEnabled ? 'ausschalten' : 'einschalten'}`"/>
            </template>
            <template>
              <div v-if="!hasAtLeastOneStrategy">
                <p>
                  <fin-lexicon-text auto-solve
                                    text="Das Monitoring stellt sicher, dass dein Portfolio der eingestellten Strategie folgt."/>
                </p>
                <p>
                  Dazu muss mindestens eine Assetklasse einer Strategie folgen.
                  Navigiere zur entsprechenden Assetklasse
                  in deinem Portfolio,
                  <span v-if="selectedAssetClass">
                    und ordne eine Strategie zu.
                    Hier kannst du für die Assetklasse {{ selectedAssetClass.name }} direkt eine Strategie zuordnen:
                    <br/>
                    <v-btn text @click="showChooseStrategy">
                      Strategie wählen
                    </v-btn>
                  </span>
                  <span v-else>und ordne eine Strategie per 'Strategie wählen' zu.</span>
                </p>
                <div class="d-flex float-right">
                  <v-btn class="mt-8 mb-3 submitBtn"
                         color="fin-color-primary-dark"
                         dark
                         @click="showMonitoringManagementDialog = false">
                    Alles klar
                  </v-btn>
                </div>
              </div>
              <div v-else>
                <p>
                  <fin-lexicon-text auto-solve
                                    text="Das Monitoring stellt sicher, dass dein Portfolio der eingestellten Strategie folgt."/>
                </p>
                <p>
                  Mit dem hiesigen Schalter kannst Du das Monitoring deines Portfolios
                  {{ monitoringEnabled ? 'ausschalten' : 'einschalten' }}.
                </p>

                <v-switch v-model="monitoringEnabled"
                          :color="$finColors.primaryDark"
                          :false-value="false"
                          :label="monitoringEnabled ? 'aktiviert' : 'deaktiviert'"
                          :style="{marginRight: '40px'}"
                          :true-value="true"
                          class="mt-0 pt-0"
                          hide-details
                          inset/>
                <div class="d-flex float-right">
                  <v-btn class="mt-8 mb-3 mr-3 submitBtn"
                         color="fin-color-primary-dark"
                         dark
                         @click="cancelPortfolioMonitoring()">
                    Abbrechen
                  </v-btn>
                  <v-btn class="mt-8 mb-3 submitBtn"
                         color="fin-color-primary-dark"
                         dark
                         @click="updatePortfolioMonitoring(portfolio, monitoringEnabled)">
                    Übernehmen
                  </v-btn>
                </div>
              </div>
            </template>
          </fin-dialog>

          <fin-tabs :selected-tab="selectedTab"
                    :tab-items="mainTabItems"
                    @tabChangeNotAllowed="(val,index) => tabChangeNotAllowed(val, index)"
                    @tabChanged="val => tabChanged(val)">
            <template #0>
              <fin-subscription-content v-if="isInactivePortfolio"
                                        :feature-id="featureIds.PORTFOLIO.id"
                                        :max-active-elements="maxPortfolios"
                                        :restriction-type="featureRestrictions.INACTIVE"
                                        style="max-width: 700px;"/>

              <div v-else
                   class="text-right">
                <v-btn v-if="$vuetify.breakpoint.smOnly"
                       class="mr-2 mb-3"
                       dark
                       @click="manageMonitoring('monitoringButton')">
                  <v-icon v-if="isMonitoringAllowed" class="mr-1">icon-notification</v-icon>
                  <fin-subscription-lock v-else/>
                  Monitoring
                </v-btn>
                <v-btn v-if="$vuetify.breakpoint.smAndDown"
                       class="mb-3"
                       dark
                       :disabled="!hasInstrumentsEntries"
                       @click="gotoTabWithId('BACKTESTING')">
                  <fin-subscription-lock v-if="!isBacktestingEnabled"/>
                  Backtesting
                </v-btn>
              </div>
              <fin-portfolio-overview-part :portfolio-data="portfolio"
                                           :portfolio-ratios="portfolioTimeSeriesRT"
                                           :isInactivePortfolio="isInactivePortfolio"
                                           @openTransactionHistory="openTransactionHistory"
                                           @updatePortfolio="reloadPortfolio"
                                           @addToPortfolioDialog="proceedToAddToPortfolio"
                                           @gotoTabWithId="val => gotoTabWithId(val)"
                                           v-if="selectedTab===0 && !isInactivePortfolio"
              />
            </template>

            <template v-for="(assetClass, index) in assetClasses"
                      v-slot:[index+1]>
              <fin-portfolio-overview-part :key="assetClass.id"
                                           :asset-class="assetClass"
                                           :distribution="profile.assetDistribution"
                                           :portfolio-data="portfolio"
                                           :portfolio-ratios="portfolioTimeSeriesRT"
                                           :isInactivePortfolio="isInactivePortfolio"
                                           @openTransactionHistory="openTransactionHistory"
                                           @updatePortfolio="reloadPortfolio"
                                           @addToPortfolioDialog="proceedToAddToPortfolio"
                                           v-if="selectedTab===index+1"
              >
                <template #pieChart>
                  <h4 style="font-size: 24px">{{
                      chartTitle.find(item => item.assetClass === assetClass.id).title
                    }}</h4>
                  <fin-chart2 :options="getDistributionChartOptions(assetClass)"
                              class="fin-portfolio-pie-chart" constructor-type="chart"/>
                </template>
              </fin-portfolio-overview-part>
            </template>

            <template v-slot:[assetClasses.length+1]>
              <fin-portfolio-backtesting :portfolio-data="portfolio"/>
            </template>
          </fin-tabs>

          <v-container fluid v-if="portfolio">
            <v-row v-if="selectedTab===0 && hasInstrumentsEntries"
                   class="fin-color-primary-light pt-10 my-12 mb-0"
                   v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
                   v-style:paddingRight="$valueByBreakpoint($finPadding.right)">
              <v-col :class="$vuetify.breakpoint.xs ? 'pb-0' : ''" cols="12" sm="8">
                <p>Hier kannst du demnächst die Transaktionen deines finCraft Portfolios direkt per Wertpapierorder an
                  ausgewählte Broker übertragen.
                </p>
              </v-col>
              <v-col
                :class="$vuetify.breakpoint.xs ? 'pt-0 text-center' : 'text-right'"
                cols="12" sm="4">
                <v-btn
                  :to="{name: 'createOrderList'}"
                  dark
                  :disabled="isInactivePortfolio"
                  @click.native="createOrderListClicked">
                  Orderliste erstellen
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </div>
        <div v-else class="fin-portfolio-add-to-portfolio">
          <fin-add-to-portfolio :instruments="[addToPortfolioItem]"
                                :title="'Wertpapier in Portfolio hinzufügen'"
                                :to="{name: 'portfolioDetail', params: {id: portfolio.id}}"/>
        </div>
      </div>

      <div v-else-if="loadingPortfolio">
        <v-row class="pt-10 fin-color-primary-light"
               v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
               v-style:paddingRight="$valueByBreakpoint($finPadding.right)">
          <v-col cols="12" sm="7" lg="9">
            <fin-logo-full dark loading/>
          </v-col>
        </v-row>
      </div>

      <div v-else>
        <v-container :style="fullPageStyle" fill-height fluid ma-0>
          <v-row align="center" justify="center">
            <fin-error-msg msg="Dein Portfolio konnte nicht geladen werden. Probier es bitte nochmals."/>
          </v-row>
        </v-container>
      </div>
    </fin-data-loader>

    <router-view v-else/>
  </div>
</template>

<script>
import {mapGetters, mapState} from 'vuex'
import {finPortfolioAPI} from '@/js/finAPI'
import {onUpdatePortfolioRealTimeChange, onUpdatePortfolioStatus} from "@/js/finSubscriptions"
import AssetClassFinder from '@/components/pages/onboarding/AssetClassFinder'
import AssetClasses from '@/mocks/AssetClasses'
import GoBackMixin from '@/mixins/GoBack'
import SubscriptionHandler from "@/mixins/SubscriptionHandler"
import FinPortfolioOverviewPart from '@/components/pages/myPortfolio/PortfolioOverviewPart/index'
import FinEditableProperty from "@/components/pages/profile/EditableProperty"
import FinLogoFull from "../../../components/common/logo/LogoFull/index"
import FinAddToPortfolio from "@/components/common/portfolio/AddToPortfolio/index"
import FinChooseSelector from "@/components/common/ChooseSelector/index"
import FinDataLoader from "@/components/common/DataLoader/index"
import FinTabs from "@/components/common/Tabs"
import FinChart2 from "@/components/common/Chart2/FinChart2"
import FinBanner from "@/components/common/Banner"
import FinRealtimeIndicator from "@/components/common/RealtimeIndicator"
import FinLogoSign from "@/components/common/logo/LogoSign"
import AmplitudePush from "@/mixins/AmplitudePush"
import FinTransactionsListDialog from "@/components/common/transactions/ListDialog"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import {exportPortfolio} from "@/js/finMutations"
import FinErrorMsg from "@/components/common/ErrorMsg"
import finBrowser from "@/js/finBrowser"
import FinPortfolioBacktesting from "@/components/pages/myPortfolio/PortfolioBacktesting"
import FinLexiconText from "@/components/common/lexicon/LexiconText"
import PriceModel from "@/mixins/PriceModel"
import {PRICEMODEL_FEATURE_IDS, PRICEMODEL_FEATURE_RESTRICTIONS} from "@/js/finPriceModel"
import {EventBus} from "@/plugins/fincraft"
import FinSubscriptionContent from "@/components/common/subscription/content"
import FinSubscriptionLock from "@/components/common/subscription/lock"

export default {
  name: 'PortfolioDetail',
  components: {
    FinSubscriptionLock,
    FinSubscriptionContent,
    FinLexiconText,
    FinPortfolioBacktesting,
    FinErrorMsg,
    FinTransactionsListDialog,
    FinLogoSign,
    FinRealtimeIndicator,
    FinBanner,
    FinChart2,
    FinTabs,
    FinDataLoader,
    FinChooseSelector,
    FinAddToPortfolio,
    FinLogoFull,
    FinEditableProperty,
    FinPortfolioOverviewPart
  },
  mixins: [GoBackMixin, SubscriptionHandler, AmplitudePush, PriceModel],
  props: {
    id: {
      type: String
    },
    tab: {
      type: String
    }
  },
  data() {
    return {
      isIos: finBrowser.IS_IOS(),
      isMobile: finBrowser.IS_MOBILE(),
      showDialogCash: false,
      selectedAssetClass: '',
      addToPortfolioDialog: false,
      editMode: false,
      portfolio: undefined,
      assetClasses: AssetClasses.items,
      addToPortfolio: false,
      addToPortfolioItem: undefined,
      selectedTab: 0,
      loadingPortfolio: true,
      portfolioDistribution: undefined,
      showPortfolioStatusBanner: false,
      selectedViewId: 'DEFAULT',
      subscriptionMessages: {
        portofolioStatusUpdates: []
      },
      portfolioTimeSeriesRT: [
        {assetClass: 'TOTAL', ratios: undefined},
        {assetClass: 'SHARE', ratios: undefined},
        {assetClass: 'SHARE_ETF', ratios: undefined},
        {assetClass: 'CORPORATE_BONDS_ETF', ratios: undefined},
        {assetClass: 'GOVERNMENT_BONDS_ETF', ratios: undefined},
        {assetClass: 'COMMODITIES_ETF', ratios: undefined}
      ],
      chartTitle: [
        {assetClass: 'SHARE', title: 'Branchenverteilung'},
        {assetClass: 'SHARE_ETF', title: 'Regionverteilung'},
        {assetClass: 'CORPORATE_BONDS_ETF', title: 'Regionverteilung'},
        {assetClass: 'GOVERNMENT_BONDS_ETF', title: 'Länderverteilung'},
        {assetClass: 'COMMODITIES_ETF', title: 'Rohstoffverteilung'}
      ],
      showTransactionsDialog: false,
      showMonitoringManagementDialog: false,
      monitoringEnabled: undefined,
      featureIds: PRICEMODEL_FEATURE_IDS,
      featureRestrictions: PRICEMODEL_FEATURE_RESTRICTIONS,
    }
  },
  computed: {
    ...mapGetters('auth', ['userId', 'profile']),
    ...mapState({
      profile: state => state.auth.profile
    }),
    isInactivePortfolio() {
      return this.portfolio ? this.portfolio.inactive === true : true
    },
    mainTabItems() {
      const assetClassesNames = this.assetClasses.map((assetClass) => {
        return {
          name: assetClass.name,
          color: assetClass.color,
          id: assetClass.id,
          disabled: this.isInactivePortfolio
        }
      })
      return [
        {name: "Übersicht", id: 'OVERVIEW-TAB'},
        ...assetClassesNames,
        {
          name: 'Backtesting', id: 'BACKTESTING',
          disabled: !this.hasInstrumentsEntries || this.isInactivePortfolio,
          allowed: this.isBacktestingEnabled
        }]
    },
    fullPageStyle() {
      return {'min-height': `calc( 100vh - ${this.$vuetify.application.top}px)`}
    },
    instruments: {
      get() {
        let instruments = undefined
        if (this.portfolio && this.assetClass === undefined) {
          instruments = [...this.portfolio.shareEntries.items].concat(this.portfolio.etfEntries.items)
        } else if (this.portfolio && this.assetClassId === 'SHARE' && this.portfolio.shareEntries) {
          instruments = this.portfolio.shareEntries.items
        } else if (this.portfolio && this.assetClassId.includes("ETF") && this.portfolio.etfEntries) {
          instruments = this.getPortfolioEtfInstrumentsByAssetClass
        } else {
          instruments = []
        }
        return instruments
      },
      set(val) {
        if (this.portfolio) {
          this.portfolio.shareEntries.items = val.filter(item => item.assetClass === 'SHARE')
          this.portfolio.etfEntries.items = val.filter(item => item.assetClass !== 'SHARE')
        }
      }
    },
    hasInstrumentsEntries() {
      try {
        return this.portfolio.shareEntries.items.length > 0 || this.portfolio.etfEntries.items.length > 0
      } catch (e) {
        return false
      }
    },
    assetDistribution() {
      return AssetClassFinder.all
    },
    computedBannerMessagesList() {
      return this.subscriptionMessages.portofolioStatusUpdates.filter(item => {
        return item.status && item.status.calculationStatus !== 'OK'
      })
    },
    excelDownloadEnabled() {
      if (this.isInactivePortfolio) {
        return true
      }
      return this.portfolio && this.portfolio.status && this.portfolio.status.calculationStatus === 'OK'
    },
    hasAtLeastOneStrategy() {
      console.log("hasAtLeastOneStrategy this.portfolio", this.portfolio)
      return this.portfolio.shareStrategy || this.portfolio.shareETFStrategy ||
      this.portfolio.corporateBondsETFStrategy || this.portfolio.governmentBondsETFStrategy
      || this.portfolio.commoditiesETFStrategy ? true : false
    },
    maxPortfolios() {
      return this.priceModelEnabledAndLimit(PRICEMODEL_FEATURE_IDS.PORTFOLIO.id)
    },
    isAutoDividendenServiceEnabled() {
      return this.isPriceModelFeatureEnabled(PRICEMODEL_FEATURE_IDS.AUTO_DIVIDEND_SERVICE.id)
    },
    isBacktestingEnabled() {
      return this.isPriceModelFeatureEnabled(PRICEMODEL_FEATURE_IDS.BACKTESTING.id)
    },
    isMonitoringAllowed() {
      return this.isPriceModelFeatureEnabled(PRICEMODEL_FEATURE_IDS.MONITORING.id)
    }
  },
  methods: {
    showDividendenServiceAboUpgradeDialog() {
      EventBus.$emit('showSubscriptionPopup', PRICEMODEL_FEATURE_IDS.AUTO_DIVIDEND_SERVICE.id, PRICEMODEL_FEATURE_RESTRICTIONS.UNAVAILABLE, {useLexiconForFeature: true})
    },
    tabChangeNotAllowed(tab, index) {
      if (tab.name === 'Backtesting') {
        EventBus.$emit('showSubscriptionPopup', PRICEMODEL_FEATURE_IDS.BACKTESTING.id, PRICEMODEL_FEATURE_RESTRICTIONS.UNAVAILABLE)
      }
    },
    manageTransactions() {
      this.trackToAmplitude('contextmenu', 'manage_transactions')
      this.showTransactionsDialog = true
    },
    manageMonitoring(positionOnPage) {
      if (this.isMonitoringAllowed) {
        this.trackToAmplitude(positionOnPage, 'manage_monitoring')
        this.showMonitoringManagementDialog = true
      } else {
        EventBus.$emit('showSubscriptionPopup', PRICEMODEL_FEATURE_IDS.MONITORING.id, PRICEMODEL_FEATURE_RESTRICTIONS.UNAVAILABLE)
      }
    },
    showChooseStrategy() {
      this.pushToAmplitude({
        'event': 'fincraft_portfolio_details_monitoringDialog_choose_strategy',
        'step': 'choose_strategy',
        'placement': 'fincraft_portfolio_details',
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
      const goTo = {
        name: this.selectedAssetClass.id === 'SHARE' ? 'finStrategy_list_shares_portfolio' : 'finStrategy_list_etfs_portfolio',
        params: {
          mode: 'chooseStrategy',
          portfolioId: this.portfolio.id,
          assetClassID: this.selectedAssetClass.id,
          goThenToPage: 'portfolioDetail'
        }
      }
      this.$router.push(goTo)
    },
    getDistributionChartOptions(assetClass) {
      const data = this.portfolioDistribution?.find(item => item.assetClass === assetClass.id).distribution
      return {
        chart: {
          type: 'pie'
        },
        credits: {
          enabled: false,
        },
        series: [
          {
            data
          },
        ],
        tooltip: {
          formatter: function () {
            return this.key
          }
        }
      }
    },
    proceedToAddToPortfolio() {
      if (this.selectedAssetClass) {
        this.submitAssetClass()
      } else {
        this.addToPortfolioDialog = true
      }
    },
    trackToAmplitude(positionOnPage, action, stepChoice) {
      const event = {
        'event': 'fincraft_portfolio_details_' + positionOnPage + "_" + action,
        'step': action,
        'placement': 'portfolio_details_' + positionOnPage,
        'vue_breakpoint': this.$vuetify.breakpoint.name
      }
      if (stepChoice !== undefined) {
        event.step_choice = stepChoice
      }
      this.pushToAmplitude(event)
    },
    startDeletePortfolio() {
      this.trackToAmplitude('contextmenu', 'delete_portfolio')
      this.$confirm({
        title: 'Portfolio löschen',
        text: `Möchtest du das Portfolio ${this.portfolio.name} wirklich löschen?`,
        dialogWidth: 448,
        confirmBtn: 'Löschen'
      })
        .then(() => {
          this.deletePortfolio(this.portfolio)
            .then(() => {
              // Todo loading state in dialog
              this.$router.push({name: 'myFinHub'})
              this.$toasted.global.finToast({
                message: 'Das Portfolio %name% wurde erfolgreich gelöscht.',
                placeholders: [{name: 'name', content: this.portfolio.name}],
                type: 'success'
              })
            })
            .catch((e) => {
              console.error('deletePortfolio failed', e)
              this.$toasted.global.finToast({
                message: `Beim Löschen deines Portfolios %name% ist ein Fehler aufgetreten.`,
                placeholders: [{name: "name", content: this.portfolio.name}],
                type: 'error'
              })
            })
        })
    },
    async deletePortfolio(portfolio) {
      await finPortfolioAPI.deletePortfolioConfiguration(portfolio)
    },
    async updatePortfolioName(portfolio, name) {
      try {
        const result = await finPortfolioAPI.renamePortfolio(portfolio.id, name)
        portfolio = Object.assign(portfolio, result)
      } catch (error) {
        console.error("error updating portfolio name", error)
        this.$toasted.global.finToast({
          message: `Beim Aktualisieren deines Portfolios %name% ist ein Fehler aufgetreten.`,
          placeholders: [{name: "name", content: portfolio.name}],
          type: 'error'
        })
      }
    },
    cancelPortfolioMonitoring() {
      // reset and close
      this.monitoringEnabled = !this.portfolio.deactivateFinAlert
      this.showMonitoringManagementDialog = false
    },
    async updatePortfolioMonitoring(portfolio, enableMonitoring) {
      await finPortfolioAPI.updatePortfolioMonitoring(portfolio.id, enableMonitoring)
        .then(data => {
          const disabled = data === null ? true : data.deactivateFinAlert
          this.portfolio.deactivateFinAlert = disabled
          this.monitoringEnabled = !disabled
          this.showMonitoringManagementDialog = false
          this.$toasted.global.finToast({
            message: `Das Monitoring deines Portfolios %name% wurde ${this.monitoringEnabled ? 'aktiviert' : 'deaktiviert'}.`,
            placeholders: [{name: 'name', content: portfolio.name}],
            type: 'success'
          })
        })
        .catch(error => {
          console.error("Failed to update portfolio monitoring", error)
          this.$toasted.global.finToast({
            message: `Beim Aktualisieren des Monitorings deines Portfolios %name% ist ein Fehler aufgetreten.`,
            placeholders: [{name: "name", content: portfolio.name}],
            type: 'error'
          })
        })
    },
    showRename() {
      this.trackToAmplitude('contextmenu', 'rename_portfolio')
      this.$refs.rename.show()
    },
    loadPortfolio() {
      finPortfolioAPI.getPortfolio(this.id).then((portfolio) => {
        if (portfolio) {
          this.portfolio = portfolio
          this.loadingPortfolio = false

          // deactivateFinAlert => enabled Mapping  :  true => false ,  false => true , null,leer => false
          this.monitoringEnabled = (typeof portfolio.deactivateFinAlert === "boolean") ? !portfolio.deactivateFinAlert : false

          if (portfolio.status && portfolio.status.calculationStatus !== 'OK') {
            this.subscriptionMessages.portofolioStatusUpdates = [{
              id: portfolio.id,
              status: portfolio.status
            }]
          } else if (portfolio.status && portfolio.status.calculationStatus === 'OK') {
            this.subscriptionMessages.portofolioStatusUpdates = []
          }

          this.showPortfolioStatusBanner = this.computedBannerMessagesList.length > 0

          if (!portfolio.inactive) {
            this.loadPortfolioTimeSeriesRTLastValues()
            this.loadPortfolioDistributionData()
          }
        } else {
          this.loadingPortfolio = false
          throw new Error('the portfolio could not be loaded ' + this.id)
        }
      }).catch((e) => {
        console.error('TODO track and log loadPortfolio', e)
      })
    },
    async reloadPortfolio() {
      console.log("reloadPortfolio")
      if (this.$refs.loader) {
        await this.$refs.loader._fetchData()
      }
    },
    handlePortfolioRealTimeSubscription(newData, item) {
      console.log("handlePortfolioRealTimeSubscription newData.data.onUpdatePortfolioRealTimeChange ==> pull data", newData.data.onUpdatePortfolioRealTimeChange, item)
      if (newData.data.onUpdatePortfolioRealTimeChange.id === this.portfolio.id) {
        this.reloadPortfolio()
      }
    },
    handlePortfolioStatusSubscription(newData, item) {
      // update banner information and displayed data
      console.log("handlePortfolioStatusSubscription newData.data.onUpdatePortfolio", newData.data.onUpdatePortfolio, item)

      let status = newData.data.onUpdatePortfolio.status ?
        newData.data.onUpdatePortfolio.status.calculationStatus : undefined
      if (status === undefined || status === 'OK') {
        this.subscriptionMessages.portofolioStatusUpdates = []

        let addedCashAmount = newData.data.onUpdatePortfolio.status.addedCashAmount
        if (addedCashAmount && addedCashAmount > 0) {
          this.$toasted.global.finToast({
            message: `Der Cash deines Portfolios %name% wurde automatisch um ${this.$options.filters.currency(addedCashAmount)} erhöht.`,
            placeholders: [{name: 'name', content: this.portfolio.name}],
            type: 'info'
          })
        }
        this.reloadPortfolio()
      } else {
        this.subscriptionMessages.portofolioStatusUpdates = [newData.data.onUpdatePortfolio]
      }

      this.portfolio.status = newData.data.onUpdatePortfolio.status
    },
    unsubscribePortfolioMutations() {
      this.unsubscribeChanges()
    },
    subscribePortfolioMutations() {
      // subscribe to portfolio status changes
      this.subscribeToChanges(
        this.portfolio,
        onUpdatePortfolioStatus,
        (pf) => {
          return {owner: this.userId, id: pf.id}
        },
        this.handlePortfolioStatusSubscription
      )

      this.subscribeToChanges(this.portfolio,
        onUpdatePortfolioRealTimeChange,
        (pf) => {
          return {owner: this.userId, id: pf.id}
        },
        this.handlePortfolioRealTimeSubscription)
    },
    getPortfolioStatusReadableMessage(status) {
      if (status === 'FAILED') {
        return 'Bei der Berechnung deines Portfolios ist ein Fehler aufgetreten. Der finCraft-Support wurde informiert und arbeitet an einer Lösung.'
      } else if (status === ' OK') {
        return 'Die Daten zu deinem Portfolio sind aktuell.'
      } else {
        return 'Dein Portfolio wird gerade neu berechnet.'
      }
    },
    loadPortfolioTimeSeriesRTLastValues() {
      // dont't load timeSeriesRT for 'backtesting' tab
      if (this.selectedTab !== this.assetClasses.length + 1) {
        // selectedTab === 0 => TOTAL or
        // use selectedAssetClass (set at tab change via click) or
        // use tab (url param set via router)
        const ac = this.selectedTab === 0 ? 'TOTAL' : this.selectedAssetClass ? this.selectedAssetClass.id : this.tab
        if (this.portfolio) {
          finPortfolioAPI.getPortfolioTimeSeriesRTLastValues(this.portfolio.id, ac).then((data) => {
            if (data) {
              console.log("finPortfolioAPI.getPortfolioTimeSeriesRTLastValues for assetClass", ac)
              const rt = this.portfolioTimeSeriesRT?.find(item => item.assetClass === ac)
              if (rt) {
                rt.ratios = data
              } else {
                console.error("failed to set timeSeriesRT for assetClass", ac)
              }
              console.log("finPortfolioAPI.getPortfolioTimeSeriesRTLastValues for assetClass rt", ac, rt)
            }
          })
        }
      }
    },
    loadPortfolioDistributionData() {
      finPortfolioAPI.getPortfolioDistribution(this.portfolio.id).then((data) => {
          if (data) {
            this.portfolioDistribution = []
            this.assetClasses.forEach(assetClass => {
              let res = data.find(item => item.assetClass === assetClass.id).distribution.map(item => {
                return {name: item.name + ' ' + item.percentage + "%", y: item.percentage}
              })
              this.portfolioDistribution.push({assetClass: assetClass.id, distribution: res})
            })
          }
        }
      ).catch((e) => {
          console.error('TODO track and log loadPortfolioDistributionData', e)
          // TODO error
        }
      )
    },
    submitAssetClass(selectedItem) {
      this.selectedAssetClass = selectedItem ? this.assetClasses.find(item => item.id === selectedItem) : this.selectedAssetClass
      this.addToPortfolioDialog = false
      this.$router.push({
        name: 'addInstrumentsToPortfolio',
        params: {
          strategy: this.selectedAssetClass.id === 'SHARE' ? this.portfolio.shareStrategy :
            this.selectedAssetClass.id === 'SHARE_ETF' ? this.portfolio.shareETFStrategy :
              this.selectedAssetClass.id === 'CORPORATE_BONDS_ETF' ? this.portfolio.corporateBondsETFStrategy :
                this.selectedAssetClass.id === 'GOVERNMENT_BONDS_ETF' ? this.portfolio.governmentBondsETFStrategy :
                  this.selectedAssetClass.id === 'COMMODITIES_ETF' ? this.portfolio.commoditiesETFStrategy : undefined,
          assetClassId: this.selectedAssetClass.id,
          shareUniverse: this.portfolio.shareUniverse,
          to: {name: 'portfolioDetail', params: {id: this.portfolio.id, tab: this.selectedTab}}
        }
      })
    },
    gotoTabWithId(tabId) {
      const priceModelFeatureId = PRICEMODEL_FEATURE_IDS[tabId]?.id
      if (priceModelFeatureId && this.isPriceModelFeatureEnabled(priceModelFeatureId) === false) {
        EventBus.$emit('showSubscriptionPopup', priceModelFeatureId, PRICEMODEL_FEATURE_RESTRICTIONS.UNAVAILABLE)
      } else {
        this.selectedTab = this.mainTabItems.findIndex(t => t.id === tabId)
      }
    },
    tabChanged(val) {
      const stepChoice = this.mainTabItems[val].name
      this.trackToAmplitude('tabs', 'change_tab', stepChoice)
      this.selectedTab = val
    },
    contextMenuClicked() {
      this.trackToAmplitude('contextmenu', 'open_context_menu')
    },
    addToPortfolioClicked() {
      this.trackToAmplitude('contextmenu', 'add_instruments_to_portfolio')
      this.addToPortfolioDialog = true
    },
    createOrderListClicked() {
      this.trackToAmplitude('orderlist', 'create_orderlist')
    },
    async exportExcel() {
      this.trackToAmplitude('contextmenu', 'export_portfolio')
      if (this.isPriceModelFeatureEnabled(PRICEMODEL_FEATURE_IDS.PORTFOLIO_UPLOAD_DOWNLOAD.id) === false) {
        EventBus.$emit('showSubscriptionPopup', PRICEMODEL_FEATURE_IDS.PORTFOLIO_UPLOAD_DOWNLOAD.id, PRICEMODEL_FEATURE_RESTRICTIONS.UNAVAILABLE)
      } else {
        let exportURL = await API.graphql(graphqlOperation(exportPortfolio, {
          portfolioId: this.portfolio.id,
          format: 'XLSX',
        }))

        if (exportURL.data.exportPortfolio) {
          const fileName = exportURL.data.exportPortfolio.substr(exportURL.data.exportPortfolio.indexOf('/Download') + 1)
          const signedURL = await this.$Amplify.Storage.get(fileName, {level: 'private'})

          if (signedURL) {
            const a = document.createElement("a")
            a.href = signedURL
            a.setAttribute('download', exportURL.data.exportPortfolio.substr(exportURL.data.exportPortfolio.lastIndexOf('/') + 1))
            a.target = '_blank'

            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }
        }
      }
    },
    openTransactionHistory() {
      this.showTransactionsDialog = true
      this.selectedViewId = 'BUYSELL'
    }
  },
  watch: {
    tab: {
      handler() {
        const selected = this.mainTabItems.findIndex((item) => {
          return item.id === this.tab
        })
        // Show Overview tab if nothing is found
        this.selectedTab = selected === -1 ? 0 : selected
      },
      immediate: true,
    },
    selectedTab() {
      if (!this.selectedTab || this.selectedTab === 0 || this.selectedTab > this.assetClasses.length) {
        this.selectedAssetClass = undefined
      } else {
        this.selectedAssetClass = this.assetClasses[this.selectedTab - 1]
      }
      if (this.portfolio) {
        this.loadPortfolioTimeSeriesRTLastValues()
      }
    },
    portfolio() {
      this.unsubscribePortfolioMutations()
      this.subscribePortfolioMutations()
    },
    showTransactionsDialog(isShowing) {
      if (!isShowing) {
        this.selectedViewId = 'DEFAULT'
      }
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .fin-portfolio-contextmenu.v-btn--disabled .v-icon {
  color: $fin-color-primary-dark !important;
  opacity: 0.7 !important;
}

.last-change-text {
  font-size: 1em;
  color: $fin-color-cool-grey
}

a {
  color: $fin-color-primary-dark;
  text-decoration: none !important;
}

.realtime {
  position: relative !important;
  top: -7px !important;
  left: 5px !important;
}
</style>
